import * as XLSX from "xlsx";

// ─── Column validation ────────────────────────────────────────────────────────

/**
 * Expected fixed columns at positions 0 and 1.
 * All subsequent non-empty columns must be day headers or "Employee" repeats.
 */
const EXPECTED_COL_0 = "role";
const EXPECTED_COL_1 = "employee";
const VALID_DAY_PREFIXES = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/**
 * Validates that the header row matches the required template structure:
 *  - Column A: "Role"
 *  - Column B: "Employee"
 *  - Columns C+: Day headers ("Mon DD/MM" … "Sun DD/MM") or "Employee" repeat.
 *    Unknown headers are only tolerated AFTER the last detected day-column block
 *    (i.e. trailing summary columns are ignored).
 *
 * Throws a descriptive Error if any violation is found.
 */
function validateHeaderColumns(headerRow: string[]): void {
  const col0 = (headerRow[0] ?? "").toString().trim();
  const col1 = (headerRow[1] ?? "").toString().trim();

  if (col0.toLowerCase() !== EXPECTED_COL_0) {
    throw new Error(
      `Column A must be "Role" but found "${col0 || "(empty)"}". ` +
        "Please download and use the sample template."
    );
  }

  if (col1.toLowerCase() !== EXPECTED_COL_1) {
    throw new Error(
      `Column B must be "Employee" but found "${col1 || "(empty)"}". ` +
        "Please download and use the sample template."
    );
  }

  // Scan cols 2+ — stop tolerance once we hit the first non-day / non-repeat column
  // (mirrors detectWeekBlocks behaviour). Any unknown header *before* that stop
  // is a violation.
  let encounteredDayCol = false;
  let dayBlockClosed = false; // true after the first break/stop point

  for (let i = 2; i < headerRow.length; i++) {
    const cell = (headerRow[i] ?? "").toString().trim();
    if (!cell) continue; // merged / blank header cells are fine

    const lower = cell.toLowerCase();
    const isDay = VALID_DAY_PREFIXES.some((p) => lower.startsWith(p));
    const isRepeat = lower === "employee";

    if (isDay || isRepeat) {
      encounteredDayCol = true;
      continue;
    }

    // Unknown header found
    if (!dayBlockClosed) {
      if (!encounteredDayCol) {
        // Unknown column before any day column → definite template violation
        throw new Error(
          `Unexpected column "${cell}" at position ${i + 1}. ` +
            "Columns after Employee must follow the format \"Mon DD/MM\", \"Tue DD/MM\", etc. " +
            "Please download and use the sample template."
        );
      }
      // Unknown column after some day columns → treat as the trailing-summary stop
      dayBlockClosed = true;
    }
    // Once dayBlockClosed, silently ignore trailing columns (e.g. "Total Shifts")
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DayShift {
  day: string;   // "Mon", "Tue", …
  date: string;  // "16/02"
  value: string; // "07:00" | "Off" | "Leave" | "-" | custom code
}

export interface ScheduleWeek {
  weekLabel: string; // "Week 1 (16/02 – 22/02)"
  days: DayShift[];  // exactly 7
}

export interface ScheduleRow {
  id: string;
  role: string;
  employee: string;
  employee_id?: string | null; // resolved from API after file upload
  weeks: ScheduleWeek[];
}

export interface ParsedSchedule {
  fileName: string;
  parsedAt: string;
  weekCount: number;
  rows: ScheduleRow[];
}

// ─── Day labels ───────────────────────────────────────────────────────────────

const DAY_PREFIX_RE = /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)/i;
const DATE_RE = /(\d{2}\/\d{2})/;

/**
 * Auto-detect week blocks by scanning the header row from column 2 onward.
 *
 * The Excel layout repeats for each week:
 *   [ "Mon DD/MM", "Tue DD/MM", "Wed DD/MM", "Thu DD/MM", "Fri DD/MM", "Sat DD/MM", "Sun DD/MM" ]
 * Between weeks there is an "Employee" repeat column, or the columns simply follow directly.
 * Scanning stops when a column header is neither a day-of-week nor a repeat "Employee" column.
 *
 * Returns an array of { startCol, days: [{ col, day, date }][] } — one entry per detected week.
 */
function detectWeekBlocks(
  headerRow: string[]
): Array<{ weekLabel: string; dayColumns: Array<{ col: number; day: string; date: string }> }> {
  const blocks: Array<{
    weekLabel: string;
    dayColumns: Array<{ col: number; day: string; date: string }>;
  }> = [];

  let col = 2; // start after Role (0) and Employee (1)
  let currentDays: Array<{ col: number; day: string; date: string }> = [];

  while (col < headerRow.length) {
    const cell = (headerRow[col] ?? "").toString().trim();

    if (!cell) {
      col++;
      continue;
    }

    // If the header starts with a day name and contains a date → it's a shift day
    if (DAY_PREFIX_RE.test(cell)) {
      const dateMatch = cell.match(DATE_RE);
      const dayMatch = cell.match(DAY_PREFIX_RE);
      currentDays.push({
        col,
        day: dayMatch ? dayMatch[1].charAt(0).toUpperCase() + dayMatch[1].slice(1, 3) : cell.slice(0, 3),
        date: dateMatch ? dateMatch[1] : "",
      });

      // Once we have 7 days, close this week
      if (currentDays.length === 7) {
        const first = currentDays[0].date;
        const last = currentDays[6].date;
        const weekNum = blocks.length + 1;
        blocks.push({
          weekLabel: first && last
            ? `Week ${weekNum} (${first} – ${last})`
            : `Week ${weekNum}`,
          dayColumns: [...currentDays],
        });
        currentDays = [];
      }
      col++;
      continue;
    }

    // "Employee" repeat header: skip
    if (cell.toLowerCase() === "employee") {
      col++;
      continue;
    }

    // Anything else (e.g. "Total Shifts", "Wk 1", "Balance"…) → stop
    break;
  }

  // If we have a partial week (< 7 days) at the end, still include it
  if (currentDays.length > 0) {
    const first = currentDays[0].date;
    const last = currentDays[currentDays.length - 1].date;
    const weekNum = blocks.length + 1;
    blocks.push({
      weekLabel: first ? `Week ${weekNum} (${first} – ${last})` : `Week ${weekNum}`,
      dayColumns: [...currentDays],
    });
  }

  return blocks;
}

// ─── Normalisation ────────────────────────────────────────────────────────────

/** Canonical display value for a raw Excel cell */
export function normaliseShift(raw: string): string {
  const v = (raw ?? "").toString().trim();
  if (!v || v === "-") return "-";
  if (v.toUpperCase() === "OFF") return "Off";
  if (v.toUpperCase() === "LEAVE") return "Leave";
  return v;
}

// ─── Non-employee row filter ──────────────────────────────────────────────────

/**
 * Returns true for rows that look like footer/legend entries rather than
 * real employee names:
 *   - Time values: "07:00", "08:00 AM"
 *   - Labels ending with ":" e.g. "TOTAL STAFF:", "DETAILS:"
 *   - Single all-uppercase tokens with no spaces e.g. "CLOSING", "TOTAL"
 */
function isNonEmployeeRow(value: string): boolean {
  // Time pattern: optional digits, colon, two digits, optional AM/PM
  if (/^\d{1,2}:\d{2}(\s*[AaPp][Mm])?$/.test(value)) return true;
  // Label ending with colon
  if (value.endsWith(":")) return true;
  // Single token, all uppercase, no lowercase letters (e.g. "CLOSING")
  if (/^[^a-z\s]+$/.test(value) && !/\s/.test(value)) return true;
  return false;
}

// ─── Parser ───────────────────────────────────────────────────────────────────

export function parseScheduleExcel(file: File): Promise<ParsedSchedule> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rawRows: any[][] = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          defval: "",
        });

        if (rawRows.length < 2) throw new Error("Sheet is empty or too short.");

        const headerRow = rawRows[0].map((c: any) => (c ?? "").toString());

        // ── Column validation ────────────────────────────────────────────────
        validateHeaderColumns(headerRow);

        // ── Auto-detect week blocks ──────────────────────────────────────────
        const weekBlocks = detectWeekBlocks(headerRow);

        if (weekBlocks.length === 0) {
          throw new Error(
            "Could not detect any weekly schedule columns. " +
              'Ensure headers follow the format "Mon DD/MM", "Tue DD/MM", etc.'
          );
        }

        // ── Parse employee rows ──────────────────────────────────────────────
        const rows: ScheduleRow[] = [];

        for (let ri = 1; ri < rawRows.length; ri++) {
          const row = rawRows[ri];

          // Skip entirely empty rows (no cell in the row has any content).
          const isEntirelyEmpty = !Array.isArray(row) || row.every((c) => c === "" || c == null);
          if (isEntirelyEmpty) continue;

          const role = (row[0] ?? "").toString().trim();
          const employee = (row[1] ?? "").toString().trim();

          // Skip rows where the employee cell is empty (empty cells between data rows).
          if (!employee) continue;

          // Skip non-employee rows: time values, labels ending with ":", or
          // all-uppercase single tokens that are clearly not names.
          if (isNonEmployeeRow(employee)) continue;

          const weeks: ScheduleWeek[] = weekBlocks.map((block) => ({
            weekLabel: block.weekLabel,
            days: block.dayColumns.map(({ col, day, date }) => ({
              day,
              date,
              value: normaliseShift((row[col] ?? "").toString()),
            })),
          }));

          rows.push({ id: `${employee}_${ri}`, role, employee, weeks });
        }

        resolve({
          fileName: file.name,
          parsedAt: new Date().toISOString(),
          weekCount: weekBlocks.length,
          rows,
        });
      } catch (err: any) {
        reject(new Error(`Failed to parse Excel: ${err.message}`));
      }
    };

    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsArrayBuffer(file);
  });
}

// ─── Shift display helpers ────────────────────────────────────────────────────

export type ShiftCategory = "morning" | "afternoon" | "off" | "leave" | "special" | "empty";

export function getShiftCategory(value: string): ShiftCategory {
  const v = value.trim();
  if (!v || v === "-") return "empty";
  if (v === "Off") return "off";
  if (v === "Leave") return "leave";
  const timeMatch = v.match(/^(\d{1,2}):(\d{2})/);
  if (timeMatch) {
    return parseInt(timeMatch[1], 10) < 12 ? "morning" : "afternoon";
  }
  return "special";
}

export const SHIFT_COLORS: Record<ShiftCategory, { bg: string; text: string; label: string; borderColor: string }> = {
  morning:   { bg: "#e8f5e9", text: "#2e7d32", label: "Morning shift",   borderColor: "#a5d6a7" },
  afternoon: { bg: "#e3f2fd", text: "#1565c0", label: "Afternoon shift", borderColor: "#90caf9" },
  off:       { bg: "#f5f5f5", text: "#9e9e9e", label: "Off – no shift",  borderColor: "#e0e0e0" },
  leave:     { bg: "#fff8e1", text: "#f57f17", label: "Leave – 8 hrs",   borderColor: "#ffe082" },
  special:   { bg: "#f3e5f5", text: "#6a1b9a", label: "Special duty",    borderColor: "#ce93d8" },
  empty:     { bg: "#fafafa", text: "#bdbdbd", label: "—",               borderColor: "#eeeeee" },
};
